'use client'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { observer } from 'mobx-react-lite'

import { ClassCard } from '@/domains/Account/modules/Mktu/components'

import classNames from 'classnames'
import styles from './ClassButton.module.scss'
import { MktuClassStore } from '@/store/Mktu/MktuClass'

type ClassButtonProps = {
  className?: string
  onSelect: (classNumber: MktuClassStore['uuid']) => void
  onClickButton: (classNumber: MktuClassStore['uuid']) => void
  mktuClass: MktuClassStore
  isSelected?: boolean
  isReadonly?: boolean
}
export const ClassButton: FunctionComponent<ClassButtonProps> = observer(
  ({
    className,
    onSelect,
    onClickButton,
    mktuClass,
    isSelected,
    isReadonly = false,
  }) => {
    const click = useCallback(() => {
      onClickButton(mktuClass.uuid)
    }, [mktuClass.uuid, onClickButton])

    const _isSelected = isSelected ?? mktuClass.selectedWordsNames.length !== 0
    const isButton = typeof onSelect === 'function'
    const tooltipRef = useRef<HTMLDivElement>(undefined)
    const [tooltipOffsetRight, setTooltipOffsetRight] = useState(0)
    const tooltipOffsetRightRef = useRef(0)
    const [isHover, setIsHover] = useState(false)
    const mouseEnterTimer = useRef(null)
    const mouseLeaveTimer = useRef(null)

    const calcTooltipPosition = useCallback(() => {
      const tooltipElement = tooltipRef.current
      if (tooltipElement != null) {
        const position = tooltipElement.getBoundingClientRect()
        const width = window.innerWidth
        const diffRight = width - position.right

        const offsetRight =
          -1 * Math.min(0, diffRight - tooltipOffsetRightRef.current)

        tooltipOffsetRightRef.current = offsetRight
        setTooltipOffsetRight(offsetRight)
      }
    }, [])

    useEffect(() => {
      if (typeof window !== null) {
        calcTooltipPosition()
        window.addEventListener('resize', calcTooltipPosition)
        return () => window.removeEventListener('resize', calcTooltipPosition)
      }
    }, [calcTooltipPosition])

    const onMouseEnter = useCallback(() => {
      if (mouseEnterTimer.current != null) {
        clearTimeout(mouseEnterTimer.current)
      }
      if (mouseLeaveTimer.current != null) {
        clearTimeout(mouseLeaveTimer.current)
      }
      mouseEnterTimer.current = setTimeout(() => {
        calcTooltipPosition()
        setIsHover(true)
      }, 300)
    }, [calcTooltipPosition])

    const onMouseLeave = useCallback(() => {
      if (mouseEnterTimer.current != null) {
        clearTimeout(mouseEnterTimer.current)
      }
      if (mouseLeaveTimer.current != null) {
        clearTimeout(mouseLeaveTimer.current)
      }
      mouseLeaveTimer.current = setTimeout(() => {
        setIsHover(false)
      }, 300)
    }, [])

    return (
      <div
        className={classNames(className, styles.container, {
          [styles.isSelected]: _isSelected,
          [styles.isHover]: isHover,
        })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {isButton ? (
          <button
            type="button"
            className={classNames(styles.value, {
              [styles.isHover]: isHover,
            })}
            onClick={click}
          >
            {mktuClass.number}
          </button>
        ) : (
          <div
            className={classNames(styles.value, {
              [styles.isHover]: isHover,
            })}
          >
            {mktuClass.number}
          </div>
        )}
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{
            transform: `translateX(calc(-50% - ${tooltipOffsetRight}px))`,
          }}
        >
          <ClassCard
            mktuClass={mktuClass}
            onClick={onSelect}
            isReadonly={isReadonly}
          />
        </div>
      </div>
    )
  }
)
